const API_URL = 'https://api.payhook.link'
const DASHBOARD_URL = 'https://dashboard.payhook.link'
const SESSION_EMAIL_KEY = 'payhook:authEmail'
const ACCOUNT_ID_KEY = 'payhook:accountId'

function isAuthenticated () {
  return Boolean(getStoredAuthEmail() || window.localStorage.getItem(ACCOUNT_ID_KEY))
}

let modalEl = null

async function authRequest (path, body) {
  const response = await window.fetch(`${API_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {})
  })

  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    const message = payload?.error?.message || 'Something went wrong. Try again.'
    throw new Error(message)
  }

  return payload
}

function rememberAuthEmail (email) {
  if (email) window.localStorage.setItem(SESSION_EMAIL_KEY, email)
}

export function getStoredAuthEmail () {
  return window.localStorage.getItem(SESSION_EMAIL_KEY)
}

export function clearStoredAuthEmail () {
  window.localStorage.removeItem(SESSION_EMAIL_KEY)
}

function ensureModal () {
  if (modalEl) return modalEl

  modalEl = document.createElement('dialog')
  modalEl.className = 'connect-lead'
  modalEl.id = 'connect-lead-dialog'
  modalEl.innerHTML = `
    <form class="connect-lead__form" method="dialog">
      <div class="connect-lead__header">
        <h2 class="connect-lead__title" id="connect-lead-title">Continue to Payhook</h2>
        <button type="button" class="connect-lead__close" id="connect-lead-close" aria-label="Close">×</button>
      </div>

      <div class="connect-lead__panel" id="connect-lead-panel-auth">
        <p class="connect-lead__intro" id="connect-lead-intro">Enter your email and we will send you a sign-in link to open the dashboard.</p>
        <label class="connect-lead__field">
          <span class="connect-lead__label">Email</span>
          <input class="connect-lead__input" type="email" name="email" autocomplete="email" required placeholder="you@company.com">
        </label>
        <p class="connect-lead__error" id="connect-lead-error" hidden></p>
        <div class="connect-lead__actions">
          <button class="btn btn--primary" type="submit" id="connect-lead-submit">
            <span class="btn__spinner" aria-hidden="true"></span>
            <span class="btn__label">Email me a sign-in link</span>
          </button>
        </div>
      </div>

      <div class="connect-lead__panel" id="connect-lead-panel-sent" hidden>
        <p class="connect-lead__intro" id="connect-lead-sent-intro">Check your inbox for a sign-in link from Payhook. Open it to continue to your dashboard.</p>
        <div class="connect-lead__actions">
          <button class="btn btn--ghost" type="button" id="connect-lead-resend">
            <span class="btn__spinner" aria-hidden="true"></span>
            <span class="btn__label">Send another link</span>
          </button>
        </div>
      </div>
    </form>
  `

  document.body.appendChild(modalEl)

  const form = modalEl.querySelector('.connect-lead__form')
  const authPanel = modalEl.querySelector('#connect-lead-panel-auth')
  const sentPanel = modalEl.querySelector('#connect-lead-panel-sent')
  const sentIntro = modalEl.querySelector('#connect-lead-sent-intro')
  const errorEl = modalEl.querySelector('#connect-lead-error')
  const submitBtn = modalEl.querySelector('#connect-lead-submit')
  const resendBtn = modalEl.querySelector('#connect-lead-resend')
  const closeBtn = modalEl.querySelector('#connect-lead-close')
  const emailInput = modalEl.querySelector('input[name="email"]')

  let pendingEmail = ''

  const SUBMIT_LABEL = 'Email me a sign-in link'
  const SUBMIT_LOADING_LABEL = 'Sending…'
  const RESEND_LABEL = 'Send another link'
  const RESEND_LOADING_LABEL = 'Sending…'

  function setButtonLoading (button, loading, { idleLabel, loadingLabel }) {
    const label = button.querySelector('.btn__label')
    button.classList.toggle('is-loading', loading)
    button.setAttribute('aria-busy', loading ? 'true' : 'false')
    if (label) label.textContent = loading ? loadingLabel : idleLabel
  }

  function setBusy (busy, activeButton = null) {
    emailInput.disabled = busy
    setButtonLoading(submitBtn, busy && activeButton === submitBtn, {
      idleLabel: SUBMIT_LABEL,
      loadingLabel: SUBMIT_LOADING_LABEL
    })
    setButtonLoading(resendBtn, busy && activeButton === resendBtn, {
      idleLabel: RESEND_LABEL,
      loadingLabel: RESEND_LOADING_LABEL
    })
    submitBtn.disabled = busy
    resendBtn.disabled = busy
  }

  function showError (message) {
    errorEl.textContent = message || ''
    errorEl.hidden = !message
  }

  function showAuthPanel () {
    authPanel.hidden = false
    sentPanel.hidden = true
    showError('')
    setBusy(false)
    emailInput.focus()
  }

  function showSentPanel (email) {
    pendingEmail = email
    authPanel.hidden = true
    sentPanel.hidden = false
    sentIntro.textContent = `We sent a sign-in link to ${email}. Open it to continue to your dashboard. The link expires in 15 minutes.`
    setBusy(false)
  }

  async function sendMagicLink (email) {
    await authRequest('/auth/magic-link', { email })
    rememberAuthEmail(email)
    showSentPanel(email)
  }

  closeBtn.addEventListener('click', () => {
    modalEl.close()
  })

  modalEl.addEventListener('click', (event) => {
    if (!form.contains(event.target)) modalEl.close()
  })

  modalEl.addEventListener('close', () => {
    setBusy(false)
    showError('')
  })

  resendBtn.addEventListener('click', async () => {
    const email = pendingEmail || emailInput.value.trim().toLowerCase()
    if (!email) {
      showAuthPanel()
      return
    }

    setBusy(true, resendBtn)
    try {
      await sendMagicLink(email)
    } catch (error) {
      showAuthPanel()
      showError(error.message || 'Could not send sign-in link.')
      setBusy(false)
    }
  })

  form.addEventListener('submit', async (event) => {
    event.preventDefault()
    showError('')

    const email = emailInput.value.trim().toLowerCase()
    setBusy(true, submitBtn)

    try {
      await sendMagicLink(email)
    } catch (error) {
      showError(error.message || 'Could not send sign-in link.')
      setBusy(false)
    }
  })

  modalEl.openAuth = ({ email } = {}) => {
    pendingEmail = ''
    emailInput.value = email || getStoredAuthEmail() || ''
    showAuthPanel()
  }

  return modalEl
}

function openAuthModal () {
  const dialog = ensureModal()

  if (typeof dialog.showModal === 'function') {
    dialog.showModal()
  } else {
    dialog.setAttribute('open', '')
  }

  dialog.openAuth()
}

function handleConnectClick (event) {
  if (isAuthenticated()) return

  event.preventDefault()
  openAuthModal()
}

export function initConnectCta () {
  document.querySelectorAll('[data-connect-cta]').forEach((el) => {
    if (el.dataset.connectBound === '1') return
    el.dataset.connectBound = '1'
    el.setAttribute('href', DASHBOARD_URL)
    el.addEventListener('click', handleConnectClick)
  })
}
